import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

/**
 * Cast editor (owner/admin). Lists speaking characters by line count with their voice; each voice
 * can be auditioned and changed. Saving re-renders in the background — only the lines whose voice
 * changed are synthesized (every other take is reused), and the player reloads in place when the
 * new read is ready.
 *
 *   GET  {base}/cast   { narrator, characters[], voices[], rerendering, rendering_enabled }
 *   POST {base}/cast   { changes: { CHARACTER: voice_id, __NARRATOR__: voice_id } }
 */
const AGE = { child: 'child', teen: 'teen', '20s': '20s', '30s-40s': '30s–40s', '50s-60s': '50s–60s', '70+': '70+' };

export default function CastEditor({ base, onClose, onSaved, onSample }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const sample = useRef(null);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    axios.get(`${base}/cast`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.error ?? e.message));
  }, [base]);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    addEventListener('keydown', k, true); return () => removeEventListener('keydown', k, true);
  }, [onClose]);
  useEffect(() => () => sample.current?.pause(), []);

  const voiceBy = useMemo(() => Object.fromEntries((data?.voices ?? []).map((v) => [v.id, v])), [data]);
  const current = (who, original) => edits[who] ?? original;
  const changed = Object.keys(edits).length;

  /**
   * Hear one voice. The READ IS PAUSED FIRST (owner, 2026-09-23: "play buttons don't work" — a
   * 3-second sample under a read in full flow is inaudible), and a failure is shown instead of
   * being swallowed, so a blocked or missing sample says so.
   */
  const play = (voiceId) => {
    const v = voiceBy[voiceId];
    if (!v?.sample_url) { setErr('That voice has no sample yet. Run tableread:voice-samples.'); return; }
    onSample?.();                                        // stop the read: one thing plays at a time
    if (!sample.current) {
      sample.current = new Audio();
      sample.current.onended = () => setPlaying(null);
      sample.current.onerror = () => { setPlaying(null); setErr('That sample would not play in this browser.'); };
    }
    if (playing === voiceId) { sample.current.pause(); setPlaying(null); return; }
    setErr(null);
    sample.current.src = v.sample_url;
    sample.current.play().then(() => setPlaying(voiceId))
      .catch((e) => { setPlaying(null); setErr(`The sample could not play (${e.name}).`); });
  };
  const setVoice = (who, original, v) => setEdits((e) => {
    const n = { ...e };
    if (v === original) delete n[who]; else n[who] = v;
    return n;
  });

  const save = () => {
    setSaving(true); setErr(null);
    axios.post(`${base}/cast`, { changes: edits })
      .then(() => { onSaved?.(changed); onClose(); })
      .catch((e) => { setErr(e?.response?.data?.error ?? e.message); setSaving(false); });
  };

  // A plain function, not a component: a component defined in render remounts every row per keystroke.
  const row = ({ who, label, sub, voice, review }) => {
    const v = current(who, voice);
    return (
      <li key={who} className={`tr-cast-row ${edits[who] ? 'changed' : ''}`}>
        <div className="tr-cast-who">
          <span className="tr-cast-name">{label}</span>
          <span className="tr-cast-sub">{sub}{review && <span className="tr-cast-flag"> · voice may be the wrong gender — please check</span>}</span>
        </div>
        <div className="tr-cast-pick">
          <select value={v} onChange={(e) => setVoice(who, voice, e.target.value)} aria-label={`Voice for ${label}`}>
            {data.voices.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <button type="button" className="tr-btn tr-cast-play" onClick={() => play(v)} disabled={!voiceBy[v]?.sample_url}
                  aria-label={`Hear ${voiceBy[v]?.label ?? v}`} title={voiceBy[v]?.sample_url ? 'Hear this voice' : 'No sample yet'}>
            {playing === v ? '❚❚' : '▶'}
          </button>
        </div>
      </li>
    );
  };

  return (
    <div className="tr-fb-backdrop" onClick={onClose}>
      <div className="tr-fb tr-cast" role="dialog" aria-modal="true" aria-labelledby="tr-cast-h" onClick={(e) => e.stopPropagation()}>
        <h2 id="tr-cast-h" className="tr-fb-h">Cast</h2>
        <p className="tr-dim">Voices were assigned automatically. Change any of them; only the changed parts are re-recorded.</p>
        {err && <p className="tr-err">{err}</p>}
        {!data && !err && <p className="tr-dim">Loading cast…</p>}
        {data && (
          <>
            {data.rerendering && <p className="tr-progress">A cast change is already being recorded. New changes will replace it.</p>}
            <ul className="tr-cast-list">
              {row({ who: '__NARRATOR__', label: 'Narrator', sub: 'scene headings and action', voice: data.narrator })}
              {data.characters.map((c) => row({ who: c.character, label: c.character,
                sub: `${c.lines} line${c.lines === 1 ? '' : 's'}${AGE[c.age_band] ? ` · ${AGE[c.age_band]}` : ''}`,
                voice: c.voice, review: c.needs_review }))}
            </ul>
          </>
        )}
        <div className="tr-fb-actions">
          <button className="tr-btn" onClick={onClose}>Cancel</button>
          <button className="tr-btn tr-play" onClick={save}
                  disabled={!changed || saving || (data && !data.rendering_enabled)}
                  title={data && !data.rendering_enabled ? 'Re-rendering is switched off right now' : undefined}>
            {saving ? 'Saving…' : changed ? `Save ${changed} change${changed === 1 ? '' : 's'}` : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export const CAST_CSS = `
.tr-cast{max-width:640px;max-height:calc(100% - 32px);display:flex;flex-direction:column}
.tr-cast-list{list-style:none;padding:0;margin:12px 0 0;overflow-y:auto;min-height:0;border-top:1px solid #e3edf1}
.tr-cast-row{display:flex;gap:12px;align-items:center;justify-content:space-between;padding:10px 4px;border-bottom:1px solid #eef3f5}
.tr-cast-row.changed{background:#f2f7f9}
.tr-cast-who{display:flex;flex-direction:column;min-width:0}
.tr-cast-name{font-weight:650;color:#102d38;font-size:.92rem;letter-spacing:.03em}
.tr-cast-sub{font-size:.8rem;color:#5c7a8a}
.tr-cast-flag{color:#8a5a00;font-weight:600}
.tr-cast-pick{display:flex;gap:6px;align-items:center;flex:0 0 auto}
.tr-cast-pick select{font:inherit;font-size:.86rem;border:1px solid #cfdde4;border-radius:8px;color:#14536e;background:#fff;padding:6px 8px;max-width:240px}
.tr-cast-play{padding:6px 10px;min-width:38px}
.tr-cast-play:disabled{opacity:.4;cursor:default}
@media (max-width:560px){
  .tr-cast-row{flex-direction:column;align-items:stretch;gap:6px}
  .tr-cast-pick select{max-width:none;flex:1}
}
`;
